// apps/client/src/hooks/useBookChurnReport.ts
/**
 * Hook to fetch Book Churn Report from BigQuery
 *
 * Returns KPIs, trends, broker breakdown, and individual discharge events.
 * Discharges are split into RETAINED (kept via a confirmed refinance match) vs
 * TRUE CHURN / LOST (no such match). Churn Rate and Net Book Growth count true
 * churn only.
 */
import { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

interface UseBookChurnReportParams {
  companyId: string;
  year: number;
  yearType?: 'calendar' | 'financial';
  brokerId?: string;
  officeId?: string;
  entityCode?: string;
}

interface DischargeEvent {
  loanId: string;
  customerName: string;
  dischargeDate: string;
  settlementDate: string;
  broker: string;
  lender: string | null;
  monthsToDischarge: number;
  isClawbackPeriod: boolean;
  clawbackAmountCents: number;
  isRetained: boolean;
}

interface MonthlyTrend {
  month: string;
  totalUpfronts: number;
  trueChurnCount: number;
  retainedCount: number;
  netGrowth: number;
}

interface BrokerBreakdown {
  brokerId: string;
  brokerDisplay: string;
  totalUpfronts: number;
  trueChurnCount: number;
  retainedCount: number;
  churnRate: number;
  clawbackPeriodChurnCount: number;
  trailHeadwindCents: number;
}

interface TenureCohort {
  cohort: string;
  loansInCohort: number;
  churnedCount: number;
  churnRate: number;
  avgTrailLostCents: number;
}

interface UseBookChurnReportReturn {
  // Summary KPIs
  netBookGrowthRate: number;
  totalNewClients: number;
  totalDischarged: number;
  trueChurnCount: number;
  retainedCount: number;
  churnRate: number;
  activeLoans: number;

  // Clawback analysis
  clawbackPeriodChurnCount: number;
  matureLoanChurnCount: number;
  clawbackPeriodChurnPct: number;

  // Trail impact
  totalTrailHeadwindCents: number;
  projectedAnnualTrailImpactCents: number;

  // Trends
  monthlyTrend: MonthlyTrend[];

  // Broker breakdown
  brokerBreakdown: BrokerBreakdown[];

  // Cohort analysis
  churnByTenureCohort: TenureCohort[];

  // Individual discharge events
  refinanceEvents: DischargeEvent[];

  // State
  loading: boolean;
  error: string | null;
}

export function useBookChurnReport({
  companyId,
  year,
  yearType = 'calendar',
  brokerId,
  officeId,
  entityCode,
}: UseBookChurnReportParams): UseBookChurnReportReturn {
  const [netBookGrowthRate, setNetBookGrowthRate] = useState(0);
  const [totalNewClients, setTotalNewClients] = useState(0);
  const [totalDischarged, setTotalDischarged] = useState(0);
  const [trueChurnCount, setTrueChurnCount] = useState(0);
  const [retainedCount, setRetainedCount] = useState(0);
  const [churnRate, setChurnRate] = useState(0);
  const [activeLoans, setActiveLoans] = useState(0);
  const [clawbackPeriodChurnCount, setClawbackPeriodChurnCount] = useState(0);
  const [matureLoanChurnCount, setMatureLoanChurnCount] = useState(0);
  const [clawbackPeriodChurnPct, setClawbackPeriodChurnPct] = useState(0);
  const [totalTrailHeadwindCents, setTotalTrailHeadwindCents] = useState(0);
  const [projectedAnnualTrailImpactCents, setProjectedAnnualTrailImpactCents] = useState(0);
  const [monthlyTrend, setMonthlyTrend] = useState<MonthlyTrend[]>([]);
  const [brokerBreakdown, setBrokerBreakdown] = useState<BrokerBreakdown[]>([]);
  const [churnByTenureCohort, setChurnByTenureCohort] = useState<TenureCohort[]>([]);
  const [refinanceEvents, setRefinanceEvents] = useState<DischargeEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!companyId) {
      setLoading(false);
      return;
    }

    const fetchReport = async () => {
      setLoading(true);
      setError(null);

      try {
        console.log(`[useBookChurnReport] Calling BigQuery getBookChurnReport:`, {
          companyId,
          year,
          yearType,
          brokerId: brokerId || 'all',
          officeId: officeId || 'all',
        });

        const getBookChurnReportFunction = httpsCallable(functions, 'getBookChurnReport');
        const result = await getBookChurnReportFunction({
          companyId,
          year,
          yearType,
          brokerId,
          officeId,
          entityCode,
        });

        const data = result.data as {
          netBookGrowthRate: number;
          totalNewClients: number;
          totalDischarged: number;
          trueChurnCount: number;
          retainedCount: number;
          churnRate: number;
          activeLoans: number;
          clawbackPeriodChurnCount: number;
          matureLoanChurnCount: number;
          clawbackPeriodChurnPct: number;
          totalTrailHeadwindCents: number;
          projectedAnnualTrailImpactCents: number;
          monthlyTrend: MonthlyTrend[];
          brokerBreakdown: BrokerBreakdown[];
          churnByTenureCohort: TenureCohort[];
          refinanceEvents: DischargeEvent[];
        };

        console.log(`[useBookChurnReport] BigQuery returned:`, {
          netBookGrowthRate: data.netBookGrowthRate,
          totalNewClients: data.totalNewClients,
          totalDischarged: data.totalDischarged,
          trueChurnCount: data.trueChurnCount,
          retainedCount: data.retainedCount,
          churnRate: data.churnRate,
        });

        setNetBookGrowthRate(data.netBookGrowthRate);
        setTotalNewClients(data.totalNewClients);
        setTotalDischarged(data.totalDischarged);
        setTrueChurnCount(data.trueChurnCount);
        setRetainedCount(data.retainedCount);
        setChurnRate(data.churnRate);
        setActiveLoans(data.activeLoans);
        setClawbackPeriodChurnCount(data.clawbackPeriodChurnCount);
        setMatureLoanChurnCount(data.matureLoanChurnCount);
        setClawbackPeriodChurnPct(data.clawbackPeriodChurnPct);
        setTotalTrailHeadwindCents(data.totalTrailHeadwindCents);
        setProjectedAnnualTrailImpactCents(data.projectedAnnualTrailImpactCents);
        setMonthlyTrend(data.monthlyTrend);
        setBrokerBreakdown(data.brokerBreakdown);
        setChurnByTenureCohort(data.churnByTenureCohort);
        setRefinanceEvents(data.refinanceEvents);
      } catch (err: any) {
        console.error('[useBookChurnReport] Error:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch book churn report');
        // Reset all state on error
        setNetBookGrowthRate(0);
        setTotalNewClients(0);
        setTotalDischarged(0);
        setTrueChurnCount(0);
        setRetainedCount(0);
        setChurnRate(0);
        setActiveLoans(0);
        setClawbackPeriodChurnCount(0);
        setMatureLoanChurnCount(0);
        setClawbackPeriodChurnPct(0);
        setTotalTrailHeadwindCents(0);
        setProjectedAnnualTrailImpactCents(0);
        setMonthlyTrend([]);
        setBrokerBreakdown([]);
        setChurnByTenureCohort([]);
        setRefinanceEvents([]);
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [companyId, year, yearType, brokerId, officeId, entityCode]);

  return {
    netBookGrowthRate,
    totalNewClients,
    totalDischarged,
    trueChurnCount,
    retainedCount,
    churnRate,
    activeLoans,
    clawbackPeriodChurnCount,
    matureLoanChurnCount,
    clawbackPeriodChurnPct,
    totalTrailHeadwindCents,
    projectedAnnualTrailImpactCents,
    monthlyTrend,
    brokerBreakdown,
    churnByTenureCohort,
    refinanceEvents,
    loading,
    error,
  };
}
